// Core packages

// Third party packages

// Custom packages
import { labelOf } from "src/app/modules/properties/document-archive-insert.utils";
import { ArchivePrintProperty } from "src/app/modules/properties/document-archive-print.utils";
import {
  archiveAttachedFiles,
  archiveGroupLabel,
} from "src/app/modules/properties/document-archive-row-actions.utils";
import { isArchiveObjectId } from "src/app/modules/search-docs/document-archive-general-search.utils";
import {
  DocumentArchiveTreeArea,
  DocumentArchiveTreeContent,
  DocumentArchiveTreeDoc,
  DocumentArchiveTreeGroup,
  DocumentArchiveTreeRow,
} from "src/app/shared/interfaces/document-archive-doc.interface";

/**
 * Pure helpers of «Stampa QR» on the 3.0.0 archive (FE-M10, FE-M11): which rows and which headings
 * carry a QR, the address it encodes and what the label prints around it.
 *
 * The addresses are the ones of the public page of the new archive (NEW-A3), built on identifiers
 * that survive the switch:
 *  - a document: `/qrcode/d/{_id of the row}`;
 *  - an area:    `/qrcode/a/{documentArchiveId}/{_id of the area}`;
 *  - a section:  `/qrcode/s/{documentArchiveId}/{_id of the section}`;
 *  - an element: `/qrcode/s/{documentArchiveId}/{_id of the section}/{_id of the element}`.
 * In the area of the housing units the nesting is inverted — the unit is the section level, the
 * taxonomy sections are its blocks — so a section of a unit is
 * `/qrcode/s/{documentArchiveId}/{_id of the taxonomy section}/{_id of the unit}`.
 *
 * WHAT IS OFFERED repeats the selection of the public page (`selectPublicView` in the back-end), so
 * that no sticker is printed on an address that answers «not found»: a row the page does not show,
 * a block or a section out of service, the synthetic node of the rows hanging from no element. The
 * back-end stays the authority: this only avoids printing what is known to be dead.
 *
 * NOT OFFERED, as in the legacy dossier: the contracts and the tenancy contracts (the public page does
 * not compose the aggregated contracts table, a contract is printed from its document), and the
 * privacy section. On the free plans only the area carries a QR, as in the legacy dossier.
 *
 * @since 3.0.0
 */

/**
 * Taxonomy identifiers (`title`) of the areas and sections that carry no QR of their own.
 *
 * `supplyAndServiceContracts` is the legacy `contratti`, `tenancyContracts` the legacy
 * `locazioneContrdiLocazione` (the legacy dialog printed an empty QR on both), `privacy` the legacy
 * `Aprivacy`. Identifiers, never labels: they do not change with the language nor with a rename.
 */
const NO_QR_TITLES = ["supplyAndServiceContracts", "tenancyContracts", "privacy"];

/** The i18n prefix a taxonomy title may carry; the identifier is what follows it */
const TITLE_PREFIX = "properties.documents.";

/**
 * What the QR label prints, in the language of the page. Every field is a string, empty when there is
 * nothing to print: the template prints them as they are.
 *
 * @since 3.0.0
 */
export interface DocumentArchiveQrPrintData {
  /** The address the QR encodes, always a non empty absolute URL */
  qrUrl: string;
  /** Title of the dialog: the document type, or the heading the QR was asked on */
  title: string;
  /** The code printed under the QR: the barcode of a document, empty on a heading */
  code: string;
  property: ArchivePrintProperty;
  /** When the document was filed, ISO 8601; empty on a heading */
  uploadDate: string;
  /** Name of the area */
  areaLabel: string;
  /** Name of the section */
  sectionLabel: string;
  /** Name of the element or of the unit, empty when the QR names none */
  elementLabel: string;
  /** Name of the document type, empty on a heading */
  typeLabel: string;
  /** `data.contenutoDocumento` of a document, empty on a heading */
  content: string;
}

/** A value as the label prints it: the text, or the empty string */
function textOf(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** The taxonomy identifier of a node, without the i18n prefix some nodes still carry */
function titleOf(node: { title?: unknown } | null | undefined): string {
  return node ? textOf(node.title).replace(TITLE_PREFIX, "") : "";
}

/** Whether a node is one of those that carry no QR of their own (@see NO_QR_TITLES) */
function isNoQrNode(node: { title?: unknown } | null | undefined): boolean {
  return NO_QR_TITLES.indexOf(titleOf(node)) > -1;
}

/** Whether a section level stands for a housing unit: the area of the units nests the other way */
function isUnitContent(content: DocumentArchiveTreeContent): boolean {
  return content.source === "structureObject";
}

/**
 * Address of the QR code of one document (NEW-A3): `{host}/qrcode/d/{_id of the row}`, the public
 * page of the new archive. The row's own `_id` is all it takes: no section, no `key`, no `docId`, no
 * `subDocId`, and the same address for the rows migrated from the legacy archive and the new ones.
 *
 * `""` for an id that is not an ObjectId, never a broken address.
 *
 * @since 3.0.0
 *
 * @param host  `ConfigService.frontEndHost`
 * @param rowId `_id` of the row
 */
export function documentArchiveQrDocUrl(host: string, rowId: unknown): string {
  if (!isArchiveObjectId(rowId)) {
    return "";
  }
  return `${typeof host === "string" ? host : ""}/qrcode/d/${rowId}`;
}

/** The host of the front-end as `ConfigService.frontEndHost` gives it, without a trailing slash */
function hostOf(host: unknown): string {
  return textOf(host).replace(/\/+$/, "");
}

/**
 * The address of the QR of an area, `""` when an id is not an ObjectId.
 *
 * @since 3.0.0
 *
 * @param host              `ConfigService.frontEndHost`
 * @param documentArchiveId The archive
 * @param areaId            `_id` of the area
 */
export function documentArchiveQrAreaUrl(
  host: string,
  documentArchiveId: unknown,
  areaId: unknown
): string {
  if (!isArchiveObjectId(documentArchiveId) || !isArchiveObjectId(areaId)) {
    return "";
  }
  return `${hostOf(host)}/qrcode/a/${documentArchiveId}/${areaId}`;
}

/**
 * The address of the QR of a section, or of one element of it when `structureObjectId` is given;
 * `""` when an id is not an ObjectId.
 *
 * @since 3.0.0
 *
 * @param host              `ConfigService.frontEndHost`
 * @param documentArchiveId The archive
 * @param contentId         `_id` of the taxonomy section
 * @param structureObjectId `_id` of the element or of the unit, omitted for the whole section
 */
export function documentArchiveQrSectionUrl(
  host: string,
  documentArchiveId: unknown,
  contentId: unknown,
  structureObjectId?: unknown
): string {
  if (!isArchiveObjectId(documentArchiveId) || !isArchiveObjectId(contentId)) {
    return "";
  }
  const base = `${hostOf(host)}/qrcode/s/${documentArchiveId}/${contentId}`;
  if (structureObjectId === undefined) {
    return base;
  }
  return isArchiveObjectId(structureObjectId) ? `${base}/${structureObjectId}` : "";
}

/**
 * Whether «Stampa QR» is offered on a row: the row is one the public page shows, in a block that is
 * in service.
 *
 * The public page shows a document filed, live, not expired, not demonstrative and with at least one
 * file (`isPublicRow` in the back-end); a block of an element switched off or removed is left out
 * (`group.active === false`), and so is an aggregated contract whose element is switched off
 * (`structureActive === false`). The row must carry a valid `_id`, the only thing the QR encodes.
 *
 * @since 3.0.0
 *
 * @param row             The row
 * @param group           The block the row sits in, when the caller knows it
 * @param documentActions Whether the viewer acts on documents beyond the download
 * @param live            Whether this build puts the 3.0.0 archive live (@see isArchiveV3Live)
 */
export function canPrintArchiveRowQr(
  row: DocumentArchiveTreeRow | null | undefined,
  group: DocumentArchiveTreeGroup | null | undefined,
  documentActions: boolean,
  live: boolean
): boolean {
  if (!row || !documentActions || !live) {
    return false;
  }
  if (group && group.active === false) {
    return false;
  }
  return (
    row.present === true &&
    row.expired !== true &&
    row.deleted !== true &&
    row.mockup !== true &&
    row.structureActive !== false &&
    archiveAttachedFiles(row).length > 0 &&
    isArchiveObjectId(row._id)
  );
}

/**
 * The address of the QR of an area heading, `""` when the area carries none.
 *
 * @since 3.0.0
 *
 * @param host              `ConfigService.frontEndHost`
 * @param documentArchiveId The archive
 * @param area              The area
 */
export function archiveAreaQrUrl(
  host: string,
  documentArchiveId: string,
  area: DocumentArchiveTreeArea | null | undefined
): string {
  if (!area || isNoQrNode(area)) {
    return "";
  }
  return documentArchiveQrAreaUrl(host, documentArchiveId, area._id);
}

/**
 * The address of the QR of a section heading, `""` when the section carries none: a unit (its QRs
 * are on its blocks), the synthetic node of the rows hanging from no element, a section out of
 * service, the contracts and the privacy.
 *
 * @since 3.0.0
 *
 * @param host              `ConfigService.frontEndHost`
 * @param documentArchiveId The archive
 * @param area              The area of the section
 * @param content           The section
 */
export function archiveContentQrUrl(
  host: string,
  documentArchiveId: string,
  area: DocumentArchiveTreeArea | null | undefined,
  content: DocumentArchiveTreeContent | null | undefined
): string {
  if (!area || !content || isNoQrNode(area) || isNoQrNode(content)) {
    return "";
  }
  if (isUnitContent(content) || content.active === false) {
    return "";
  }
  return documentArchiveQrSectionUrl(host, documentArchiveId, content._id);
}

/**
 * The address of the QR of a block heading, `""` when the block carries none.
 *
 * Two blocks carry one:
 *  - the block of a mapping element (`source: "structureObject"`), keyed by the element;
 *  - in a unit, the block of a taxonomy section (`source: "templateSection"`), keyed by the section,
 *    the unit being the section level (`structureKey`).
 * The block of the documents hanging from no element (`key: null`) has none: the section's is printed.
 * A block or a unit out of service has none: the public page leaves them out.
 *
 * @since 3.0.0
 *
 * @param host              `ConfigService.frontEndHost`
 * @param documentArchiveId The archive
 * @param area              The area of the block
 * @param content           The section of the block
 * @param group             The block
 */
export function archiveGroupQrUrl(
  host: string,
  documentArchiveId: string,
  area: DocumentArchiveTreeArea | null | undefined,
  content: DocumentArchiveTreeContent | null | undefined,
  group: DocumentArchiveTreeGroup | null | undefined
): string {
  if (!area || !content || !group || isNoQrNode(area) || isNoQrNode(content)) {
    return "";
  }
  if (group.active === false || content.active === false) {
    return "";
  }
  if (isUnitContent(content)) {
    // The synthetic node of the rows hanging from no unit carries no `structureKey`: without the unit
    // the address would fall back to the WHOLE section, every unit included
    if (
      group.source !== "templateSection" ||
      isNoQrNode(group) ||
      !isArchiveObjectId(content.structureKey)
    ) {
      return "";
    }
    return documentArchiveQrSectionUrl(host, documentArchiveId, group.key, content.structureKey);
  }
  if (group.source !== "structureObject") {
    return "";
  }
  return documentArchiveQrSectionUrl(host, documentArchiveId, content._id, group.key);
}

/**
 * What the label of the QR of ONE row prints, `null` when the row carries no QR
 * (@see canPrintArchiveRowQr) or the property has not answered yet.
 *
 * The block is printed as the element only when it names one: an aggregated contract copy sits in
 * the flat contracts table, whose block names nothing, and prints its `structureLabel` instead.
 *
 * @since 3.0.0
 *
 * @param input The row and where it sits, the property, the host and the language
 */
export function buildArchiveRowQrPrintData(input: {
  host: string;
  lang: string;
  property: ArchivePrintProperty | null;
  area: DocumentArchiveTreeArea | null | undefined;
  content: DocumentArchiveTreeContent | null | undefined;
  group: DocumentArchiveTreeGroup | null | undefined;
  templateDoc: DocumentArchiveTreeDoc | null | undefined;
  row: DocumentArchiveTreeRow | null | undefined;
}): DocumentArchiveQrPrintData | null {
  if (!input || !input.property || !input.row) {
    return null;
  }
  const qrUrl = documentArchiveQrDocUrl(input.host, input.row._id);
  if (!qrUrl) {
    return null;
  }
  const row = input.row;
  const data = row.data && typeof row.data === "object" ? row.data : {};
  const typeLabel = input.templateDoc ? labelOf(input.templateDoc.translation, input.lang) : "";
  const blockLabel = archiveGroupLabel(input.group, input.lang);

  return {
    qrUrl,
    title: typeLabel,
    code: textOf(data.barCode) || textOf(row.documentCode),
    property: input.property,
    uploadDate: textOf(data.createdAt) || textOf(row.createdAt),
    areaLabel: input.area ? labelOf(input.area.translation, input.lang) : "",
    sectionLabel: input.content ? labelOf(input.content.translation, input.lang) : "",
    elementLabel: blockLabel || textOf(row.structureLabel),
    typeLabel,
    content: textOf(data.contenutoDocumento),
  };
}

/**
 * What the label of the QR of a heading prints, `null` when the address is empty or the property has
 * not answered yet.
 *
 * @since 3.0.0
 *
 * @param input The address, the heading it was asked on, the property and the language
 */
export function buildArchiveHeadingQrPrintData(input: {
  qrUrl: string;
  lang: string;
  property: ArchivePrintProperty | null;
  area: DocumentArchiveTreeArea | null | undefined;
  content?: DocumentArchiveTreeContent | null;
  group?: DocumentArchiveTreeGroup | null;
}): DocumentArchiveQrPrintData | null {
  if (!input || !input.property || !textOf(input.qrUrl)) {
    return null;
  }
  const areaLabel = input.area ? labelOf(input.area.translation, input.lang) : "";
  const contentLabel = input.content ? labelOf(input.content.translation, input.lang) : "";
  const groupLabel = input.group ? archiveGroupLabel(input.group, input.lang) : "";

  // In a unit the block is the taxonomy section and the section level is the unit
  const unit = !!input.content && isUnitContent(input.content);
  const sectionLabel = unit ? groupLabel : contentLabel;
  const elementLabel = unit ? contentLabel : groupLabel;

  return {
    qrUrl: input.qrUrl,
    title: [areaLabel, sectionLabel, elementLabel].filter((part) => part !== "").pop() || "",
    code: "",
    property: input.property,
    uploadDate: "",
    areaLabel,
    sectionLabel: input.content ? sectionLabel : "",
    elementLabel: input.group ? elementLabel : "",
    typeLabel: "",
    content: "",
  };
}
